import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  Flight,
  Tv,
  Image as ImageIcon,
  Add,
  Edit,
  Delete,
  Upload,
  Event as EventIcon,
  Map as MapIcon,
  Language as LanguageIcon,
  Assessment,
  Download
} from '@mui/icons-material';
import { clubService, aircraftService, tvService, imageService, calendarService, webSourcesService, statsService } from '../services';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function ClubManagePage() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [club, setClub] = useState(null);
  const [aircraft, setAircraft] = useState([]);
  const [tvs, setTVs] = useState([]);
  const [sponsorImages, setSponsorImages] = useState([]);
  const [spotterImages, setSpotterImages] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [webSources, setWebSources] = useState([]);
  const [selectedSponsorImages, setSelectedSponsorImages] = useState([]);
  const [selectedSpotterImages, setSelectedSpotterImages] = useState([]);
  const [mapSettings, setMapSettings] = useState({
    belgium_map: { enabled: true, latitude: 50.5, longitude: 4.5, zoom: 7 },
    area_map: { enabled: true, zoom: 9 },
    plane_tracking_map: { enabled: true, zoom: 9, selected_aircraft_id: null },
    airport_activity_map: { enabled: true, zoom: 10 },
    default_map_zoom: 9,
    map_radius_km: 50,
    metar_map_extra_airports: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState('sponsor');
  const [sponsorType, setSponsorType] = useState('silver');
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);
  
  // Sponsor image edit state
  const [editImageDialogOpen, setEditImageDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editImageSponsorType, setEditImageSponsorType] = useState('silver');

  // Aircraft dialog state
  const [aircraftDialogOpen, setAircraftDialogOpen] = useState(false);
  const [editingAircraft, setEditingAircraft] = useState(null);
  const [aircraftForm, setAircraftForm] = useState({
    registration: '',
    icao24_hex: '',
    aircraft_type: '',
    nickname: '',
    notes: ''
  });
  
  // TV dialog state
  const [tvDialogOpen, setTvDialogOpen] = useState(false);
  const [editingTV, setEditingTV] = useState(null);
  
  // Calendar dialog state
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    summary: '',
    description: '',
    location: '',
    start_time: '',
    end_time: '',
    is_all_day: false
  });
  
  // Stats export state
  const [statsFrom, setStatsFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [statsTo, setStatsTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [statsDownloading, setStatsDownloading] = useState(false);
  const [statsDebug, setStatsDebug] = useState(null);

  // Web source dialog state
  const [webSourceDialogOpen, setWebSourceDialogOpen] = useState(false);
  const [editingWebSource, setEditingWebSource] = useState(null);
  const [webSourceForm, setWebSourceForm] = useState({
    name: '',
    url: '',
    description: '',
    is_active: true
  });
  const [tvForm, setTvForm] = useState({
    name: '',
    description: '',
    location_description: '',
    refresh_rate_seconds: 10,
    sponsor_ratio: {
      platinum: 3,
      gold: 3,
      silver: 3
    },
    feature_toggles: {
      platinum_sponsors: true,
      gold_sponsors: true,
      silver_sponsors: true,
      spotter_images: true,
      weather: true,
      flight_map_local: true,
      flight_tracking: true,
      calendar: true
    }
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount and when clubId changes only
  }, [clubId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load club info
      const clubData = await clubService.getClub(clubId);
      setClub(clubData);
      
      // Load aircraft
      const aircraftData = await aircraftService.getAircraft(clubId);
      setAircraft(aircraftData);
      
      // Load TVs
      const tvData = await tvService.getTVs(clubId);
      setTVs(tvData);
      
      // Load images
      const sponsorData = await imageService.getImages(clubId, 'sponsor');
      setSponsorImages(sponsorData);
      
      const spotterData = await imageService.getImages(clubId, 'spotter');
      setSpotterImages(spotterData);
      
      // Load calendar events
      try {
        const eventsData = await calendarService.getEvents(clubId);
        setCalendarEvents(eventsData);
      } catch (err) {
        console.error('Failed to load calendar events:', err);
        // Don't fail the whole page load if calendar fails
        setCalendarEvents([]);
      }
      
      // Load web sources
      try {
        const webSourcesData = await webSourcesService.getWebSources(clubId);
        setWebSources(webSourcesData);
      } catch (err) {
        console.error('Failed to load web sources:', err);
        setWebSources([]);
      }
      
      // Load map settings from club
      if (clubData.map_settings) {
        setMapSettings({
          belgium_map: { enabled: true, latitude: 50.5, longitude: 4.5, zoom: 7, ...clubData.map_settings.belgium_map },
          area_map: { enabled: true, zoom: 9, ...clubData.map_settings.area_map },
          plane_tracking_map: {
            enabled: true,
            selected_aircraft_id: null,
            ...clubData.map_settings.plane_tracking_map,
            zoom: Math.min(9, Math.max(1, Number(clubData.map_settings.plane_tracking_map?.zoom) || 9))
          },
          airport_activity_map: { enabled: true, zoom: 10, ...clubData.map_settings.airport_activity_map },
          default_map_zoom: clubData.map_settings.default_map_zoom || 9,
          map_radius_km: clubData.map_settings.map_radius_km || 50,
          metar_map_extra_airports: Array.isArray(clubData.map_settings.metar_map_extra_airports)
            ? clubData.map_settings.metar_map_extra_airports
            : []
        });
      } else {
        // Set defaults if not present
        setMapSettings({
          belgium_map: { enabled: true, latitude: 50.5, longitude: 4.5, zoom: 7 },
          area_map: { enabled: true, zoom: 9 },
          plane_tracking_map: { enabled: true, zoom: 9, selected_aircraft_id: null },
          airport_activity_map: { enabled: true, zoom: 10 },
          default_map_zoom: 9,
          map_radius_km: 50,
          metar_map_extra_airports: []
        });
      }
      
      // Clear selections if images were deleted
      setSelectedSponsorImages(prev => prev.filter(id => sponsorData.some(img => img.id === id)));
      setSelectedSpotterImages(prev => prev.filter(id => spotterData.some(img => img.id === id)));
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.error || 'Failed to load club data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (type, files) => {
    setError('');
    setSuccess('');
    setUploading(true);
    const filesArray = Array.isArray(files) ? files : Array.from(files);
    if (!filesArray.length) {
      setError('No files selected');
      setUploading(false);
      return;
    }
    setUploadProgress({ current: 0, total: filesArray.length });
    let uploaded = 0;
    let skipped = 0;
    let failed = 0;
    try {
      for (let i = 0; i < filesArray.length; i++) {
        setUploadProgress({ current: i + 1, total: filesArray.length });
        const formData = new FormData();
        formData.append('imageType', type);
        if (type === 'sponsor') {
          formData.append('sponsorType', sponsorType);
        }
        formData.append('image', filesArray[i]);
        try {
          await imageService.uploadImage(clubId, formData);
          uploaded += 1;
        } catch (err) {
          if (err.response?.status === 409 && err.response?.data?.duplicate) {
            skipped += 1;
          } else {
            failed += 1;
            console.error(`Upload failed for ${filesArray[i].name}:`, err);
          }
        }
      }
      const parts = [];
      if (uploaded > 0) parts.push(`${uploaded} uploaded`);
      if (skipped > 0) parts.push(`${skipped} skipped (duplicate filename)`);
      if (failed > 0) parts.push(`${failed} failed`);
      setSuccess(parts.length ? parts.join('; ') : 'No files uploaded.');
      loadData();
      setUploadDialogOpen(false);
      setSelectedFiles(null);
      setSponsorType('silver');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const openUploadDialog = (type) => {
    setUploadType(type);
    setSponsorType('silver');
    setSelectedFiles(null);
    setUploadDialogOpen(true);
    // Reset file input when dialog opens
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDialogFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Convert FileList to Array for easier handling
      const filesArray = Array.from(files);
      setSelectedFiles(filesArray);
    } else {
      setSelectedFiles(null);
    }
  };

  const handleDialogUpload = () => {
    if (selectedFiles && selectedFiles.length > 0) {
      handleImageUpload(uploadType, selectedFiles);
    } else {
      setError('Please select at least one image to upload');
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFiles(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenEditImage = (image) => {
    setEditingImage(image);
    setEditImageSponsorType(image.sponsor_type || 'silver');
    setEditImageDialogOpen(true);
  };

  const handleSaveEditImage = async () => {
    if (!editingImage) return;
    try {
      await imageService.updateImage(clubId, editingImage.id, { sponsor_type: editImageSponsorType });
      setEditImageDialogOpen(false);
      setEditingImage(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update sponsor category');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await imageService.deleteImage(clubId, imageId);
        loadData();
        // Remove from selected if it was selected
        setSelectedSponsorImages(prev => prev.filter(id => id !== imageId));
        setSelectedSpotterImages(prev => prev.filter(id => id !== imageId));
      } catch (err) {
        setError(err.response?.data?.error || 'Delete failed');
      }
    }
  };

  const handleToggleImageSelection = (imageId, type) => {
    if (type === 'sponsor') {
      setSelectedSponsorImages(prev => 
        prev.includes(imageId) 
          ? prev.filter(id => id !== imageId)
          : [...prev, imageId]
      );
    } else {
      setSelectedSpotterImages(prev => 
        prev.includes(imageId) 
          ? prev.filter(id => id !== imageId)
          : [...prev, imageId]
      );
    }
  };

  const handleSelectAllImages = (type) => {
    if (type === 'sponsor') {
      const allSelected = selectedSponsorImages.length === sponsorImages.length;
      if (allSelected) {
        // Deselect all
        setSelectedSponsorImages([]);
      } else {
        // Select all
        setSelectedSponsorImages(sponsorImages.map(img => img.id));
      }
    } else {
      const allSelected = selectedSpotterImages.length === spotterImages.length;
      if (allSelected) {
        // Deselect all
        setSelectedSpotterImages([]);
      } else {
        // Select all
        setSelectedSpotterImages(spotterImages.map(img => img.id));
      }
    }
  };

  // Calendar event handlers
  const openEventDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        summary: event.summary || '',
        description: event.description || '',
        location: event.location || '',
        start_time: event.start_time ? new Date(event.start_time).toISOString().slice(0, 16) : '',
        end_time: event.end_time ? new Date(event.end_time).toISOString().slice(0, 16) : '',
        is_all_day: event.is_all_day || false
      });
    } else {
      setEditingEvent(null);
      setEventForm({
        summary: '',
        description: '',
        location: '',
        start_time: '',
        end_time: '',
        is_all_day: false
      });
    }
    setEventDialogOpen(true);
  };

  const closeEventDialog = () => {
    setEventDialogOpen(false);
    setEditingEvent(null);
    setEventForm({
      summary: '',
      description: '',
      location: '',
      start_time: '',
      end_time: '',
      is_all_day: false
    });
  };

  const handleEventSubmit = async () => {
    try {
      setError('');
      if (!eventForm.summary || !eventForm.start_time) {
        setError('Event name and start time are required');
        return;
      }

      // Convert datetime-local format to ISO string for backend
      const eventData = {
        ...eventForm,
        start_time: eventForm.start_time ? new Date(eventForm.start_time).toISOString() : null,
        end_time: eventForm.end_time ? new Date(eventForm.end_time).toISOString() : (eventForm.start_time ? new Date(eventForm.start_time).toISOString() : null)
      };

      if (editingEvent) {
        await calendarService.updateEvent(clubId, editingEvent.id, eventData);
        setSuccess('Event updated successfully');
      } else {
        await calendarService.createEvent(clubId, eventData);
        setSuccess('Event added successfully');
      }
      
      closeEventDialog();
      loadData();
    } catch (err) {
      console.error('Event save failed:', err);
      setError(err.response?.data?.error || 'Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId, eventName) => {
    if (!clubId) {
      setError('Cannot delete event: Club ID is missing. Please refresh the page.');
      console.error('Delete event called without clubId:', { clubId, eventId, eventName });
      return;
    }

    if (!eventId) {
      setError('Cannot delete event: Event ID is missing');
      console.error('Delete event called without eventId:', { clubId, eventId, eventName });
      return;
    }

    if (!window.confirm(`Delete event "${eventName}"? This cannot be undone.`)) {
      return;
    }

    try {
      console.log('Deleting event:', { clubId, eventId, eventName });
      const url = `/clubs/${clubId}/events/${eventId}`;
      console.log('Delete URL:', url);
      await calendarService.deleteEvent(clubId, eventId);
      setSuccess(`Event "${eventName}" deleted successfully`);
      loadData();
    } catch (err) {
      console.error('Delete failed:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url,
        clubId,
        eventId
      });
      const errorMsg = err.response?.data?.error || err.message || 'Failed to delete event';
      setError(errorMsg);
    }
  };

  const handleDeleteAllSelected = async (type) => {
    const selected = type === 'sponsor' ? selectedSponsorImages : selectedSpotterImages;
    if (selected.length === 0) return;
    
    const count = selected.length;
    if (!window.confirm(`Are you sure you want to delete ${count} selected image(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      // Delete all selected images
      await Promise.all(
        selected.map(imageId => imageService.deleteImage(clubId, imageId))
      );
      setSuccess(`Successfully deleted ${count} image(s)`);
      // Clear selection
      if (type === 'sponsor') {
        setSelectedSponsorImages([]);
      } else {
        setSelectedSpotterImages([]);
      }
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete images');
      setLoading(false);
    }
  };

  const handleRemoveDuplicateImages = async () => {
    try {
      setError('');
      setSuccess('');
      const res = await imageService.removeDuplicates(clubId);
      const n = res?.deletedCount ?? 0;
      setSuccess(n > 0 ? `Removed ${n} duplicate image(s) (same filename).` : 'No duplicate images found.');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove duplicates');
    }
  };

  // Aircraft handlers
  const openAircraftDialog = (aircraft = null) => {
    if (aircraft) {
      setEditingAircraft(aircraft);
      setAircraftForm({
        registration: aircraft.registration || '',
        icao24_hex: aircraft.icao24_hex || '',
        aircraft_type: aircraft.aircraft_type || '',
        nickname: aircraft.nickname || '',
        notes: aircraft.notes || ''
      });
    } else {
      setEditingAircraft(null);
      setAircraftForm({
        registration: '',
        icao24_hex: '',
        aircraft_type: '',
        nickname: '',
        notes: ''
      });
    }
    setAircraftDialogOpen(true);
  };

  const closeAircraftDialog = () => {
    setAircraftDialogOpen(false);
    setEditingAircraft(null);
    setAircraftForm({
      registration: '',
      icao24_hex: '',
      aircraft_type: '',
      nickname: '',
      notes: ''
    });
  };

  const handleAircraftSubmit = async () => {
    try {
      setError('');
      if (!aircraftForm.registration) {
        setError('Registration is required');
        return;
      }

      if (editingAircraft) {
        await aircraftService.updateAircraft(clubId, editingAircraft.id, aircraftForm);
        setSuccess('Aircraft updated successfully');
      } else {
        await aircraftService.createAircraft(clubId, aircraftForm);
        setSuccess('Aircraft added successfully');
      }
      
      closeAircraftDialog();
      loadData();
    } catch (err) {
      console.error('Aircraft save failed:', err);
      setError(err.response?.data?.error || 'Failed to save aircraft');
    }
  };

  const handleDeleteAircraft = async (aircraftId, registration) => {
    if (!window.confirm(`Delete aircraft ${registration}? This cannot be undone.`)) {
      return;
    }

    try {
      await aircraftService.deleteAircraft(clubId, aircraftId);
      setSuccess(`Aircraft ${registration} deleted successfully`);
      loadData();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.response?.data?.error || 'Failed to delete aircraft');
    }
  };

  // TV handlers
  const openTVDialog = (tv = null) => {
    if (tv) {
      setEditingTV(tv);
      setTvForm({
        name: tv.name || '',
        description: tv.description || '',
        location_description: tv.location_description || '',
        refresh_rate_seconds: tv.refresh_rate_seconds || 10,
        sponsor_ratio: tv.sponsor_ratio || { platinum: 3, gold: 3, silver: 3 },
        feature_toggles: tv.feature_toggles || {
          platinum_sponsors: true,
          gold_sponsors: true,
          silver_sponsors: true,
          spotter_images: true,
          weather: true,
          flight_map_local: true,
          flight_tracking: true,
          calendar: true
        }
      });
    } else {
      setEditingTV(null);
      setTvForm({
        name: '',
        description: '',
        location_description: '',
        refresh_rate_seconds: 10,
        sponsor_ratio: { platinum: 3, gold: 3, silver: 3 },
        feature_toggles: {
          platinum_sponsors: true,
          gold_sponsors: true,
          silver_sponsors: true,
          spotter_images: true,
          weather: true,
          flight_map_local: true,
          flight_tracking: true,
          calendar: true
        }
      });
    }
    setTvDialogOpen(true);
  };

  const closeTVDialog = () => {
    setTvDialogOpen(false);
    setEditingTV(null);
    setTvForm({
      name: '',
      description: '',
      location_description: '',
      refresh_rate_seconds: 10,
      sponsor_ratio: { platinum: 3, gold: 3, silver: 3 },
      feature_toggles: {
        platinum_sponsors: true,
        gold_sponsors: true,
        silver_sponsors: true,
        spotter_images: true,
        weather: true,
        flight_map_local: true,
        flight_tracking: true,
        calendar: true
      }
    });
  };

  const handleToggleFeature = (featureName) => {
    setTvForm({
      ...tvForm,
      feature_toggles: {
        ...tvForm.feature_toggles,
        [featureName]: !(tvForm.feature_toggles?.[featureName] ?? true)
      }
    });
  };

  const handleTVSubmit = async () => {
    try {
      setError('');
      if (!tvForm.name) {
        setError('TV name is required');
        return;
      }

      if (editingTV) {
        await tvService.updateTV(clubId, editingTV.id, tvForm);
        setSuccess('TV updated successfully');
      } else {
        await tvService.createTV(clubId, tvForm);
        setSuccess('TV added successfully');
      }
      
      closeTVDialog();
      loadData();
    } catch (err) {
      console.error('TV save failed:', err);
      setError(err.response?.data?.error || 'Failed to save TV');
    }
  };

  const handleDeleteTV = async (tvId, tvName) => {
    if (!window.confirm(`Delete TV "${tvName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await tvService.deleteTV(clubId, tvId);
      setSuccess(`TV "${tvName}" deleted successfully`);
      loadData();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.response?.data?.error || 'Failed to delete TV');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  if (!club) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">Club not found</Alert>
          <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        
        <Typography variant="h4" gutterBottom>
          {club.name}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {club.airport_name} ({club.icao_code})
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3, overflow: 'visible' }}>
        <Tabs 
          value={tab} 
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons={true}
          allowScrollButtonsMobile={true}
          sx={{
            minHeight: '48px',
            width: '100%',
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': { opacity: 0.3 }
            },
            '& .MuiTabs-flexContainer': {
              gap: '4px'
            }
          }}
        >
          <Tab icon={<Assessment />} label="Statistics" />
          <Tab icon={<Flight />} label={`Aircraft (${aircraft.length})`} />
          <Tab icon={<Tv />} label={`TVs (${tvs.length})`} />
          <Tab icon={<ImageIcon />} label="Sponsor Images" />
          <Tab icon={<ImageIcon />} label="Spotter Images" />
          <Tab icon={<EventIcon />} label={`Calendar (${calendarEvents.length})`} />
          <Tab icon={<MapIcon />} label="Map Config" />
          <Tab icon={<LanguageIcon />} label={`URLs (${webSources.length})`} />
        </Tabs>
      </Paper>

      {/* Statistics Tab */}
      <TabPanel value={tab} index={0}>
        <Typography variant="h6" sx={{ mb: 2 }}>Download display statistics (CSV)</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Export what content was shown on your club&apos;s displays (aggregated by day). Select a date range (inclusive). CSV columns: date, tv_name, sponsor_image_filename, sponsor_type, content_type, event_description, count. Data older than 1 year is automatically removed.
        </Alert>
        {statsDebug !== null && (
          <Alert severity={statsDebug.totalEvents > 0 ? 'success' : 'warning'} sx={{ mb: 2 }} onClose={() => setStatsDebug(null)}>
            {statsDebug.message}
            {statsDebug.minDate && <><br />Earliest: {new Date(statsDebug.minDate).toLocaleString()}</>}
            {statsDebug.maxDate && <><br />Latest: {new Date(statsDebug.maxDate).toLocaleString()}</>}
          </Alert>
        )}
        <Paper sx={{ p: 2, maxWidth: 480 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                label="From date"
                type="date"
                fullWidth
                value={statsFrom}
                onChange={(e) => setStatsFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="To date"
                type="date"
                fullWidth
                value={statsTo}
                onChange={(e) => setStatsTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                startIcon={statsDownloading ? <CircularProgress size={20} /> : <Download />}
                disabled={statsDownloading || !statsFrom || !statsTo || statsFrom > statsTo}
                onClick={async () => {
                  setStatsDownloading(true);
                  setError('');
                  try {
                    const res = await statsService.getStatsExport(clubId, statsFrom, statsTo);
                    if (res.status < 200 || res.status >= 300) {
                      const text = await res.data.text();
                      const data = (() => { try { return JSON.parse(text); } catch (_) { return {}; } })();
                      setError(data.error || `Request failed (${res.status})`);
                      return;
                    }
                    const blob = res.data;
                    const disp = res.headers['content-disposition'];
                    const filename = (disp && disp.split('filename=')[1]) ? disp.split('filename=')[1].replace(/"/g, '').trim() : `display-stats-${statsFrom}-to-${statsTo}.csv`;
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();
                    setSuccess('CSV downloaded');
                  } catch (err) {
                    setError(err.response?.data?.error || err.message || 'Download failed');
                  } finally {
                    setStatsDownloading(false);
                  }
                }}
              >
                {statsDownloading ? 'Downloading…' : 'Download CSV'}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                onClick={async () => {
                  try {
                    const data = await statsService.getStatsDebug(clubId);
                    setStatsDebug(data);
                  } catch (e) {
                    setError(e.response?.data?.error || e.message || 'Debug failed');
                  }
                }}
              >
                Why is my CSV empty? Check if any stats were recorded
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Aircraft Tab */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Aircraft</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => openAircraftDialog()}>
            Add Aircraft
          </Button>
        </Box>
        
        {aircraft.length === 0 ? (
          <Alert severity="info">No aircraft configured yet.</Alert>
        ) : (
          <Paper>
            <List>
              {aircraft.map((plane) => (
                <ListItem key={plane.id}>
                  <ListItemText
                    primary={plane.registration}
                    secondary={
                      <>
                        {plane.aircraft_type || 'Unknown type'}
                        {plane.nickname && ` • "${plane.nickname}"`}
                        {plane.icao24_hex && ` • ${plane.icao24_hex}`}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => handleDeleteAircraft(plane.id, plane.registration)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </TabPanel>

      {/* TVs Tab */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">TV Displays</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => openTVDialog()}>
            Add TV
          </Button>
        </Box>
        
        <Grid container spacing={2}>
          {tvs.map((tv) => (
            <Grid item xs={12} md={6} key={tv.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6">{tv.name}</Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {tv.location_description || 'No location specified'}
                  </Typography>
                  {tv.description && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {tv.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" display="block">
                      Display URL:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                      {window.location.origin}/display/{tv.url_token}
                    </Typography>
                  </Box>
                  
                  <Chip
                    label={tv.is_active ? 'Active' : 'Inactive'}
                    color={tv.is_active ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 2 }}
                  />
                </CardContent>
                <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Button 
                    size="small" 
                    startIcon={<Edit />}
                    onClick={() => openTVDialog(tv)}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => handleDeleteTV(tv.id, tv.name)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {tvs.length === 0 && (
          <Alert severity="info">No TV displays configured yet.</Alert>
        )}
      </TabPanel>

      {/* Sponsor Images Tab */}
      <TabPanel value={tab} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">Sponsor Images</Typography>
            {selectedSponsorImages.length > 0 && (
              <Typography variant="body2" color="textSecondary">
                {selectedSponsorImages.length} selected
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {sponsorImages.length > 0 && (
              <Button
                variant="outlined"
                onClick={() => handleSelectAllImages('sponsor')}
              >
                {selectedSponsorImages.length === sponsorImages.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
            {selectedSponsorImages.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDeleteAllSelected('sponsor')}
              >
                Delete All ({selectedSponsorImages.length})
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={() => openUploadDialog('sponsor')}
            >
              Upload Sponsors
            </Button>
            <Button variant="outlined" onClick={handleRemoveDuplicateImages}>
              Remove duplicates
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          {sponsorImages.map((image) => (
            <Grid item xs={12} sm={6} md={4} key={image.id}>
              <Card sx={{ position: 'relative' }}>
                <Checkbox
                  checked={selectedSponsorImages.includes(image.id)}
                  onChange={() => handleToggleImageSelection(image.id, 'sponsor')}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.8)'
                  }}
                />
                <CardMedia
                  component="img"
                  height="200"
                  image={image.file_path.startsWith('http') ? image.file_path : image.file_path.startsWith('/') ? `${window.location.origin}${image.file_path}` : `${window.location.origin}/${image.file_path}`}
                  alt={image.filename}
                  onError={(e) => {
                    console.error('Failed to load image:', image.file_path);
                    e.target.style.display = 'none';
                  }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 1 }}>
                    <Chip
                      label={image.sponsor_type ? image.sponsor_type.toUpperCase() : 'SILVER'}
                      color={
                        image.sponsor_type === 'platinum' ? 'secondary' :
                        image.sponsor_type === 'gold' ? 'warning' :
                        image.sponsor_type === 'silver' ? 'default' :
                        'info'
                      }
                      size="small"
                    />
                    {image.allow_full_screen && (
                      <Chip label="Full screen OK" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="body2" noWrap>
                    {image.original_filename}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {(image.file_size_bytes / 1024).toFixed(0)} KB
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    color="primary"
                    title="Change sponsor category"
                    onClick={() => handleOpenEditImage(image)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteImage(image.id)}
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {sponsorImages.length === 0 && (
          <Alert severity="info">
            No sponsor images yet. Upload some to display on your TVs!
          </Alert>
        )}
      </TabPanel>

      {/* Spotter Images Tab */}
      <TabPanel value={tab} index={4}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">Spotter Images</Typography>
            {selectedSpotterImages.length > 0 && (
              <Typography variant="body2" color="textSecondary">
                {selectedSpotterImages.length} selected
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {spotterImages.length > 0 && (
              <Button
                variant="outlined"
                onClick={() => handleSelectAllImages('spotter')}
              >
                {selectedSpotterImages.length === spotterImages.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
            {selectedSpotterImages.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleDeleteAllSelected('spotter')}
              >
                Delete All ({selectedSpotterImages.length})
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={() => openUploadDialog('spotter')}
            >
              Upload Spotters
            </Button>
            <Button variant="outlined" onClick={handleRemoveDuplicateImages}>
              Remove duplicates
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          {spotterImages.map((image) => (
            <Grid item xs={12} sm={6} md={4} key={image.id}>
              <Card sx={{ position: 'relative' }}>
                <Checkbox
                  checked={selectedSpotterImages.includes(image.id)}
                  onChange={() => handleToggleImageSelection(image.id, 'spotter')}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.8)'
                  }}
                />
                <CardMedia
                  component="img"
                  height="200"
                  image={image.file_path.startsWith('http') ? image.file_path : image.file_path.startsWith('/') ? `${window.location.origin}${image.file_path}` : `${window.location.origin}/${image.file_path}`}
                  alt={image.filename}
                  onError={(e) => {
                    console.error('Failed to load image:', image.file_path);
                    e.target.style.display = 'none';
                  }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 1 }}>
                    {image.allow_full_screen && (
                      <Chip label="Full screen OK" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="body2" noWrap>
                    {image.original_filename}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {(image.file_size_bytes / 1024).toFixed(0)} KB
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteImage(image.id)}
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {spotterImages.length === 0 && (
          <Alert severity="info">
            No spotter images yet. Upload some aviation photos!
          </Alert>
        )}
      </TabPanel>

      {/* Calendar Tab */}
      <TabPanel value={tab} index={5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Calendar Events</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => openEventDialog()}>
            Add Event
          </Button>
        </Box>
        
        <Paper>
          <List>
            {calendarEvents.map((event) => (
              <ListItem key={event.id}>
                <ListItemText
                  primary={event.summary}
                  secondary={
                    <>
                      {new Date(event.start_time).toLocaleString()}
                      {event.location && ` • ${event.location}`}
                      {event.description && ` • ${event.description}`}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => openEventDialog(event)}
                    sx={{ mr: 1 }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => {
                      if (!event.id) {
                        setError(`Cannot delete event: Event ID is missing for "${event.summary || 'Unknown'}"`);
                        console.error('Event missing ID:', event);
                        return;
                      }
                      handleDeleteEvent(event.id, event.summary || 'Unknown Event');
                    }}
                    disabled={!event.id}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
        
        {calendarEvents.length === 0 && (
          <Alert severity="info">
            No calendar events yet. Add events to display on your TVs!
          </Alert>
        )}
      </TabPanel>

      {/* Map Configuration Tab */}
      <TabPanel value={tab} index={6}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Map Settings
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Configure map display settings for all TVs in this club. These settings apply globally to all TVs.
          </Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🇧🇪 Belgium Map (Full Screen)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Full screen map showing Belgium-wide view
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Latitude"
              type="number"
              fullWidth
              value={mapSettings.belgium_map?.latitude || 50.5}
              onChange={(e) => setMapSettings({
                ...mapSettings,
                belgium_map: {
                  ...mapSettings.belgium_map,
                  enabled: mapSettings.belgium_map?.enabled !== false,
                  latitude: parseFloat(e.target.value) || 50.5,
                  longitude: mapSettings.belgium_map?.longitude || 4.5,
                  zoom: mapSettings.belgium_map?.zoom || 7
                }
              })}
              inputProps={{ step: 0.1, min: -90, max: 90 }}
              helperText="Center latitude for Belgium map"
            />
            <TextField
              label="Longitude"
              type="number"
              fullWidth
              value={mapSettings.belgium_map?.longitude || 4.5}
              onChange={(e) => setMapSettings({
                ...mapSettings,
                belgium_map: {
                  ...mapSettings.belgium_map,
                  enabled: mapSettings.belgium_map?.enabled !== false,
                  latitude: mapSettings.belgium_map?.latitude || 50.5,
                  longitude: parseFloat(e.target.value) || 4.5,
                  zoom: mapSettings.belgium_map?.zoom || 7
                }
              })}
              inputProps={{ step: 0.1, min: -180, max: 180 }}
              helperText="Center longitude for Belgium map"
            />
            <TextField
              label="Zoom Level"
              type="number"
              fullWidth
              value={mapSettings.belgium_map?.zoom || 7}
              onChange={(e) => setMapSettings({
                ...mapSettings,
                belgium_map: {
                  ...mapSettings.belgium_map,
                  enabled: mapSettings.belgium_map?.enabled !== false,
                  latitude: mapSettings.belgium_map?.latitude || 50.5,
                  longitude: mapSettings.belgium_map?.longitude || 4.5,
                  zoom: parseInt(e.target.value) || 7
                }
              })}
              inputProps={{ min: 1, max: 20 }}
              helperText="Zoom level (1-20, lower = wider view)"
            />
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            ✈️ Plane Tracking Map (Quarter Screen)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Quarter screen map showing the position of a selected club aircraft
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth sx={{ maxWidth: 400 }}>
              <InputLabel>Select Aircraft</InputLabel>
              <Select
                value={mapSettings.plane_tracking_map?.selected_aircraft_id || ''}
                label="Select Aircraft"
                onChange={(e) => setMapSettings({
                  ...mapSettings,
                  plane_tracking_map: {
                    ...mapSettings.plane_tracking_map,
                    enabled: mapSettings.plane_tracking_map?.enabled !== false,
                    selected_aircraft_id: e.target.value || null,
                    zoom: mapSettings.plane_tracking_map?.zoom ?? 9
                  }
                })}
              >
                <MenuItem value="">None (will rotate through all aircraft)</MenuItem>
                {aircraft.filter(a => a.icao24_hex).map((plane) => (
                  <MenuItem key={plane.id} value={plane.id}>
                    {plane.registration} {plane.nickname ? `(${plane.nickname})` : ''} - {plane.icao24_hex}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Select a specific aircraft to track, or leave empty to rotate through all club aircraft
              </Typography>
            </FormControl>
            <TextField
              label="Zoom Level"
              type="number"
              fullWidth
              value={mapSettings.plane_tracking_map?.zoom ?? 9}
              onChange={(e) => setMapSettings({
                ...mapSettings,
                plane_tracking_map: {
                  ...mapSettings.plane_tracking_map,
                  enabled: mapSettings.plane_tracking_map?.enabled !== false,
                  selected_aircraft_id: mapSettings.plane_tracking_map?.selected_aircraft_id || null,
                  zoom: parseInt(e.target.value, 10) || 9
                }
              })}
              inputProps={{ min: 1, max: 9 }}
              helperText="1–9 (larger area). Map centers on the plane when the slide loads; no live follow on TV."
              sx={{ maxWidth: 400 }}
            />
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🛫 Airport Activity Map (Vertical Half Screen)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Vertical half screen map showing {club?.airport_name || 'airport'} ({club?.icao_code || 'airport code'}) with recent landings and take-offs
          </Typography>
          <TextField
            label="Zoom Level"
            type="number"
            fullWidth
            value={mapSettings.airport_activity_map?.zoom || 10}
            onChange={(e) => setMapSettings({
              ...mapSettings,
              airport_activity_map: {
                ...mapSettings.airport_activity_map,
                enabled: mapSettings.airport_activity_map?.enabled !== false,
                zoom: parseInt(e.target.value) || 10
              }
            })}
            inputProps={{ min: 1, max: 20 }}
            helperText={`Zoom level for airport activity map (uses ${club?.icao_code || 'airport'} location)`}
            sx={{ maxWidth: 400 }}
          />
          {!club?.icao_code && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No airport configured for this club. Please set an airport in the club settings to enable airport activity map.
            </Alert>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🛩️ METAR/TAF Map
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Full-screen map shows 300 km around the club airport. Zoom and pan are disabled (TV display). Extra airports below always appear on the map.
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Extra airports (always on map)</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Add airports that should always appear regardless of distance. ICAO codes (4 letters) separated by commas.
          </Typography>
          <TextField
            label="Extra ICAO Codes"
            fullWidth
            value={(mapSettings.metar_map_extra_airports || []).join(', ')}
            onChange={(e) => {
              const raw = e.target.value;
              // Parse while user is still typing — allow trailing separator
              const codes = raw
                .split(/[,\s]+/)
                .map(s => s.toUpperCase().trim())
                .filter(s => s.length > 0 && s.length <= 4);
              setMapSettings({ ...mapSettings, metar_map_extra_airports: codes });
            }}
            placeholder="e.g. EBBR, EBGN, LFMN"
            helperText="4-letter ICAO codes only. These airports will appear as orange markers on the METAR/TAF map."
            sx={{ maxWidth: 600 }}
          />
          {(mapSettings.metar_map_extra_airports || []).length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {mapSettings.metar_map_extra_airports.map((icao) => (
                <Chip
                  key={icao}
                  label={icao}
                  size="small"
                  onDelete={() => setMapSettings({
                    ...mapSettings,
                    metar_map_extra_airports: mapSettings.metar_map_extra_airports.filter(c => c !== icao)
                  })}
                  sx={{ fontFamily: 'monospace' }}
                />
              ))}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            ⚙️ General Map Settings
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Default settings for flight tracking maps
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Default Map Zoom"
              type="number"
              fullWidth
              value={mapSettings.default_map_zoom || 9}
              onChange={(e) => setMapSettings({
                ...mapSettings,
                default_map_zoom: parseInt(e.target.value) || 9
              })}
              inputProps={{ min: 1, max: 20 }}
              helperText="Zoom level for flight tracking map (1-20)"
              sx={{ maxWidth: 400 }}
            />
            <TextField
              label="Map Radius (km)"
              type="number"
              fullWidth
              value={mapSettings.map_radius_km || 50}
              onChange={(e) => setMapSettings({
                ...mapSettings,
                map_radius_km: parseInt(e.target.value) || 50
              })}
              inputProps={{ min: 1, max: 500 }}
              helperText="Radius around airport for flight tracking"
              sx={{ maxWidth: 400 }}
            />
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                setError('');
                await clubService.updateClub(clubId, { map_settings: mapSettings });
                setSuccess('Map settings saved successfully');
                loadData();
              } catch (err) {
                console.error('Failed to save map settings:', err);
                setError(err.response?.data?.error || 'Failed to save map settings');
              }
            }}
          >
            Save Map Settings
          </Button>
        </Box>
      </TabPanel>

      {/* Web Sources Tab */}
      <TabPanel value={tab} index={7}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Web Sources</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setWebSourceForm({ name: '', url: '', description: '', is_active: true });
              setEditingWebSource(null);
              setWebSourceDialogOpen(true);
            }}
          >
            Add Web Source
          </Button>
        </Box>

        <Paper>
          <List>
            {webSources.map((source) => (
              <ListItem key={source.id}>
                <ListItemText
                  primary={source.name || 'Untitled Web Source'}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                        {source.url}
                      </Typography>
                      {source.description && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                          {source.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setWebSourceForm({
                        name: source.name || '',
                        url: source.url || '',
                        description: source.description || '',
                        is_active: source.is_active !== false
                      });
                      setEditingWebSource(source);
                      setWebSourceDialogOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={async () => {
                      if (window.confirm(`Delete "${source.name || source.url}"?`)) {
                        try {
                          await webSourcesService.deleteWebSource(clubId, source.id);
                          setSuccess('Web source deleted');
                          loadData();
                        } catch (err) {
                          setError(err.response?.data?.error || 'Failed to delete web source');
                        }
                      }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>

        {webSources.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No web sources yet. Add URLs to display full-screen web pages on your TVs!
          </Alert>
        )}
      </TabPanel>

      {/* Web Source Dialog */}
      <Dialog open={webSourceDialogOpen} onClose={() => setWebSourceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingWebSource ? 'Edit Web Source' : 'Add Web Source'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name *"
            type="text"
            fullWidth
            value={webSourceForm.name}
            onChange={(e) => setWebSourceForm({ ...webSourceForm, name: e.target.value })}
            placeholder="e.g., Flight Radar, Weather Dashboard"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="URL *"
            type="url"
            fullWidth
            value={webSourceForm.url}
            onChange={(e) => setWebSourceForm({ ...webSourceForm, url: e.target.value })}
            placeholder="https://example.com"
            helperText="Full URL including http:// or https://"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={2}
            value={webSourceForm.description}
            onChange={(e) => setWebSourceForm({ ...webSourceForm, description: e.target.value })}
            placeholder="Optional description"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={webSourceForm.is_active !== false}
                onChange={(e) => setWebSourceForm({ ...webSourceForm, is_active: e.target.checked })}
              />
              <Typography variant="body2">Active (display on TVs)</Typography>
            </Box>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebSourceDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                setError('');
                if (!webSourceForm.name || !webSourceForm.url) {
                  setError('Name and URL are required');
                  return;
                }
                
                if (editingWebSource) {
                  await webSourcesService.updateWebSource(clubId, editingWebSource.id, webSourceForm);
                  setSuccess('Web source updated');
                } else {
                  await webSourcesService.createWebSource(clubId, webSourceForm);
                  setSuccess('Web source added');
                }
                setWebSourceDialogOpen(false);
                loadData();
              } catch (err) {
                setError(err.response?.data?.error || 'Failed to save web source');
              }
            }}
            variant="contained"
            disabled={!webSourceForm.name || !webSourceForm.url}
          >
            {editingWebSource ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Sponsor Category Dialog */}
      <Dialog open={editImageDialogOpen} onClose={() => setEditImageDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Sponsor Category</DialogTitle>
        <DialogContent>
          {editingImage && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {editingImage.original_filename}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Sponsor Category</InputLabel>
                <Select
                  value={editImageSponsorType}
                  label="Sponsor Category"
                  onChange={(e) => setEditImageSponsorType(e.target.value)}
                >
                  <MenuItem value="platinum">Platinum — full screen</MenuItem>
                  <MenuItem value="gold">Gold — half screen</MenuItem>
                  <MenuItem value="silver">Silver — quarter screen</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditImageDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEditImage}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload {uploadType === 'sponsor' ? 'Sponsor' : 'Spotter'} Images
        </DialogTitle>
        <DialogContent>
          {uploadType === 'sponsor' && (
            <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
              <InputLabel>Sponsor Type</InputLabel>
              <Select
                value={sponsorType}
                label="Sponsor Type"
                onChange={(e) => setSponsorType(e.target.value)}
              >
                <MenuItem value="platinum">🏆 Platinum - Premium (Full screen, always displayed)</MenuItem>
                <MenuItem value="gold">🥇 Gold - Prominent (Half screen, paired with content)</MenuItem>
                <MenuItem value="silver">🥈 Silver - Regular (Quarter screen with other content)</MenuItem>
              </Select>
            </FormControl>
          )}
          
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2 }}
          >
            Select Images
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleDialogFileSelect}
            />
          </Button>
          
          {selectedFiles && selectedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {selectedFiles.length} file(s) selected
              </Alert>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                {Array.from(selectedFiles).map((file, index) => (
                  <Box
                    key={`${file.name}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      p: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 24, color: 'grey.400', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {(file.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                {uploadProgress.total > 0
                  ? `Uploading ${uploadProgress.current} / ${uploadProgress.total} image(s)...`
                  : `Uploading image(s)... Please wait.`}
              </Alert>
              <LinearProgress
                sx={{ mt: 1 }}
                variant={uploadProgress.total > 0 ? 'determinate' : 'indeterminate'}
                value={uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} disabled={uploading}>Cancel</Button>
          <Button 
            onClick={handleDialogUpload} 
            variant="contained" 
            disabled={!selectedFiles || selectedFiles.length === 0 || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aircraft Dialog */}
      <Dialog open={aircraftDialogOpen} onClose={closeAircraftDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAircraft ? 'Edit Aircraft' : 'Add Aircraft'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Registration *"
            type="text"
            fullWidth
            value={aircraftForm.registration}
            onChange={(e) => setAircraftForm({ ...aircraftForm, registration: e.target.value.toUpperCase() })}
            placeholder="e.g., N12345, OO-ABC"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="ICAO24 Hex Code"
            type="text"
            fullWidth
            value={aircraftForm.icao24_hex}
            onChange={(e) => setAircraftForm({ ...aircraftForm, icao24_hex: e.target.value })}
            placeholder="e.g., A12345"
            helperText="Used for flight tracking"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Aircraft Type"
            type="text"
            fullWidth
            value={aircraftForm.aircraft_type}
            onChange={(e) => setAircraftForm({ ...aircraftForm, aircraft_type: e.target.value })}
            placeholder="e.g., Cessna 172, Diamond DA40"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Nickname"
            type="text"
            fullWidth
            value={aircraftForm.nickname}
            onChange={(e) => setAircraftForm({ ...aircraftForm, nickname: e.target.value })}
            placeholder="e.g., Sky Bird"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notes"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={aircraftForm.notes}
            onChange={(e) => setAircraftForm({ ...aircraftForm, notes: e.target.value })}
            placeholder="Additional information about this aircraft"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAircraftDialog}>Cancel</Button>
          <Button 
            onClick={handleAircraftSubmit} 
            variant="contained"
            disabled={!aircraftForm.registration}
          >
            {editingAircraft ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TV Dialog */}
      <Dialog open={tvDialogOpen} onClose={closeTVDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTV ? 'Edit TV Display' : 'Add TV Display'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="TV Name *"
            type="text"
            fullWidth
            value={tvForm.name}
            onChange={(e) => setTvForm({ ...tvForm, name: e.target.value })}
            placeholder="e.g., Main Lobby TV, Hangar Display"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Location Description"
            type="text"
            fullWidth
            value={tvForm.location_description}
            onChange={(e) => setTvForm({ ...tvForm, location_description: e.target.value })}
            placeholder="e.g., Main entrance, Clubhouse"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={2}
            value={tvForm.description}
            onChange={(e) => setTvForm({ ...tvForm, description: e.target.value })}
            placeholder="Additional information about this display"
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Refresh Rate (seconds)"
            type="number"
            fullWidth
            value={tvForm.refresh_rate_seconds}
            onChange={(e) => setTvForm({ ...tvForm, refresh_rate_seconds: parseInt(e.target.value) || 10 })}
            inputProps={{ min: 5, max: 300 }}
            helperText="How often content changes (5-300 seconds)"
            sx={{ mb: 3 }}
          />
          
          <Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
            Sponsor Frequency Multiplier
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Platinum Multiplier"
              type="number"
              size="small"
              value={tvForm.sponsor_ratio?.platinum || 3}
              onChange={(e) => setTvForm({
                ...tvForm,
                sponsor_ratio: {
                  ...tvForm.sponsor_ratio,
                  platinum: parseInt(e.target.value) || 3
                }
              })}
              inputProps={{ min: 1, max: 20 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Gold Multiplier"
              type="number"
              size="small"
              value={tvForm.sponsor_ratio?.gold || 3}
              onChange={(e) => setTvForm({
                ...tvForm,
                sponsor_ratio: {
                  ...tvForm.sponsor_ratio,
                  gold: parseInt(e.target.value) || 3
                }
              })}
              inputProps={{ min: 1, max: 20 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Silver Multiplier"
              type="number"
              size="small"
              value={tvForm.sponsor_ratio?.silver || 3}
              onChange={(e) => setTvForm({
                ...tvForm,
                sponsor_ratio: {
                  ...tvForm.sponsor_ratio,
                  silver: parseInt(e.target.value) || 3
                }
              })}
              inputProps={{ min: 1, max: 20 }}
              sx={{ flex: 1 }}
            />
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            Multiplier applied on top of tier size-weight. Default 3 targets ~25-30% sponsor air-time.
            Increase to show sponsors more often; all tiers use the same number for equal frequency.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
            Display Features (Enable/Disable content types)
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">🏆 Platinum Sponsors</Typography>
              <Checkbox
                checked={tvForm.feature_toggles?.platinum_sponsors ?? true}
                onChange={() => handleToggleFeature('platinum_sponsors')}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">🥇 Gold Sponsors</Typography>
              <Checkbox
                checked={tvForm.feature_toggles?.gold_sponsors ?? true}
                onChange={() => handleToggleFeature('gold_sponsors')}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">🥈 Silver Sponsors</Typography>
              <Checkbox
                checked={tvForm.feature_toggles?.silver_sponsors ?? true}
                onChange={() => handleToggleFeature('silver_sponsors')}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">📸 Spotter Images</Typography>
              <Checkbox
                checked={tvForm.feature_toggles?.spotter_images ?? true}
                onChange={() => handleToggleFeature('spotter_images')}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">🌤️ Weather Widget</Typography>
              <Checkbox
                checked={tvForm.feature_toggles?.weather ?? true}
                onChange={() => handleToggleFeature('weather')}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">🗺️ Flight Map (Local Area)</Typography>
              <Checkbox
                checked={tvForm.feature_toggles?.flight_map_local ?? true}
                onChange={() => handleToggleFeature('flight_map_local')}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">✈️ Flight Tracking (Club Aircraft)</Typography>
              <Checkbox
                checked={tvForm.feature_toggles?.flight_tracking ?? true}
                onChange={() => handleToggleFeature('flight_tracking')}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">📅 Calendar Events</Typography>
              <Checkbox
                checked={tvForm.feature_toggles?.calendar ?? true}
                onChange={() => handleToggleFeature('calendar')}
              />
            </Box>
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={closeTVDialog}>Cancel</Button>
          <Button 
            onClick={handleTVSubmit} 
            variant="contained"
            disabled={!tvForm.name}
          >
            {editingTV ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calendar Event Dialog */}
      <Dialog open={eventDialogOpen} onClose={closeEventDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEvent ? 'Edit Event' : 'Add Event'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Event Name *"
            type="text"
            fullWidth
            value={eventForm.summary}
            onChange={(e) => setEventForm({ ...eventForm, summary: e.target.value })}
            placeholder="e.g., Club Meeting, Fly-in Event"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={2}
            value={eventForm.description}
            onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
            placeholder="Event details"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Location"
            type="text"
            fullWidth
            value={eventForm.location}
            onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
            placeholder="e.g., Clubhouse, Hangar 3"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Start Time *"
            type="datetime-local"
            fullWidth
            value={eventForm.start_time}
            onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="End Time"
            type="datetime-local"
            fullWidth
            value={eventForm.end_time}
            onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to use start time"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEventDialog}>Cancel</Button>
          <Button 
            onClick={handleEventSubmit} 
            variant="contained"
            disabled={!eventForm.summary || !eventForm.start_time}
          >
            {editingEvent ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ClubManagePage;


